import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import {
  adminApi,
  adminKeys,
} from '@/features/admin/api/admin.api';
import { Button } from '@/shared/components/ui';
import { dashboardPanelSx } from '@/shared/styles/surfaces';

type FormState = {
  freeCancelHoursBeforeTravel: string;
  partialCancelHoursBeforeTravel: string;
  partialRefundPercent: string;
};

function toForm(policy: {
  freeCancelHoursBeforeTravel: number;
  partialCancelHoursBeforeTravel: number;
  partialRefundPercent: number;
}): FormState {
  return {
    freeCancelHoursBeforeTravel: String(policy.freeCancelHoursBeforeTravel),
    partialCancelHoursBeforeTravel: String(policy.partialCancelHoursBeforeTravel),
    partialRefundPercent: String(policy.partialRefundPercent),
  };
}

export function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: adminKeys.cancellationPolicy(),
    queryFn: adminApi.getCancellationPolicy,
  });

  useEffect(() => {
    if (data) setForm(toForm(data));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: adminApi.updateCancellationPolicy,
    onSuccess: (updated) => {
      setFormError(null);
      setForm(toForm(updated));
      void queryClient.invalidateQueries({ queryKey: adminKeys.cancellationPolicy() });
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to save cancellation policy';
      setFormError(message);
    },
  });

  const preview = useMemo(() => {
    if (!form) return null;
    const free = Number(form.freeCancelHoursBeforeTravel);
    const partial = Number(form.partialCancelHoursBeforeTravel);
    const percent = Number(form.partialRefundPercent);
    if (![free, partial, percent].every((n) => Number.isFinite(n))) return null;
    return { free, partial, percent };
  }, [form]);

  if (isLoading || !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load cancellation policy settings.</Alert>;
  }

  const handleSave = () => {
    const free = Number(form.freeCancelHoursBeforeTravel);
    const partial = Number(form.partialCancelHoursBeforeTravel);
    const percent = Number(form.partialRefundPercent);

    if (![free, partial, percent].every((n) => Number.isInteger(n))) {
      setFormError('All values must be whole numbers.');
      return;
    }
    if (free <= partial) {
      setFormError('Free cancel hours must be greater than partial cancel hours.');
      return;
    }
    if (percent < 0 || percent > 100) {
      setFormError('Partial refund percent must be between 0 and 100.');
      return;
    }

    setFormError(null);
    saveMutation.mutate({
      freeCancelHoursBeforeTravel: free,
      partialCancelHoursBeforeTravel: partial,
      partialRefundPercent: percent,
    });
  };

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      <AdminPageHeader
        title="Platform settings"
        subtitle="Configure the traveler cancellation policy. Changes apply to future cancellations only."
        actions={
          <Button
            intent="primary-dark"
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save policy'}
          </Button>
        }
      />

      {formError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      )}
      {saveMutation.isSuccess && saveMutation.data && !formError && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Cancellation policy saved. Now at version {saveMutation.data.policyVersion}.
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...dashboardPanelSx, p: { xs: 2.5, md: 3.5 } }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          Cancellation policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Hours are measured until the start of the travel day (UTC). Current policy version:{' '}
          <strong>{saveMutation.data?.policyVersion ?? data?.policyVersion ?? 1}</strong>
        </Typography>

        <Stack spacing={2.5}>
          <TextField
            label="Free cancel hours before travel"
            type="number"
            value={form.freeCancelHoursBeforeTravel}
            onChange={(e) =>
              setForm((prev) =>
                prev ? { ...prev, freeCancelHoursBeforeTravel: e.target.value } : prev
              )
            }
            helperText="Cancel with 100% refund when remaining hours are at or above this value."
            fullWidth
            inputProps={{ min: 1, step: 1 }}
          />
          <TextField
            label="Partial cancel hours before travel"
            type="number"
            value={form.partialCancelHoursBeforeTravel}
            onChange={(e) =>
              setForm((prev) =>
                prev ? { ...prev, partialCancelHoursBeforeTravel: e.target.value } : prev
              )
            }
            helperText="Cancel with partial refund when remaining hours are at or above this value (and below free hours)."
            fullWidth
            inputProps={{ min: 0, step: 1 }}
          />
          <TextField
            label="Partial refund percent"
            type="number"
            value={form.partialRefundPercent}
            onChange={(e) =>
              setForm((prev) =>
                prev ? { ...prev, partialRefundPercent: e.target.value } : prev
              )
            }
            helperText="Percent of the paid amount refunded in the partial window (0–100)."
            fullWidth
            inputProps={{ min: 0, max: 100, step: 1 }}
          />
        </Stack>

        {preview && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
              Live preview
            </Typography>
            <Typography variant="body2" component="div">
              • ≥ {preview.free}h before travel → cancel allowed, <strong>100%</strong> refund
              <br />• {preview.partial}h–{preview.free}h before travel → cancel allowed,{' '}
              <strong>{preview.percent}%</strong> refund
              <br />• &lt; {preview.partial}h before travel (or trip already started) → cancel blocked
            </Typography>
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
